import * as React from "react";
import { Typography, IconButton, InputAdornment, Alert, Box, Switch, FormControlLabel, Divider } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import DarkTextField from "./DarkTextField";
import SearchableSelect from "./SearchableSelect";
import AuthPrimaryButton from "./AuthPrimaryButton";
import ConsentCheckboxes from "./ConsentCheckboxes";
import PasswordStrengthChecklist from "./PasswordStrengthChecklist";
import TherapistApplyFields from "./therapistApply/TherapistApplyFields";
import { isPasswordValid } from "../utils/passwordRules";
import { fetchCountries, fetchLanguages } from "../Services/apiService";
import { AUTH_TEXT_PRIMARY, AUTH_TEXT_SECONDARY, AUTH_ERROR, BRAND_BLUE, AUTH_CARD_BORDER } from "../theme/appTheme";
import { useTranslation } from "../i18n/LanguageContext";

const emptyTherapistFields = (email) => ({
  practiceName: "",
  practiceCity: "",
  practiceEmail: email || "",
  files: [],
});

// Final step of registration: password + profile + consent, with an
// optional "register as therapist" toggle that reveals the practice/
// document fields inline. Submitting hands the whole payload back to
// Register.js, which owns the actual complete -> upload -> apply sequence.
function RegisterDetailsForm({ email, onSubmit, loading, error, fieldErrors, submitLabel }) {
  const { t } = useTranslation();
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [name, setName] = React.useState("");
  const [countryId, setCountryId] = React.useState(null);
  const [languageId, setLanguageId] = React.useState(null);
  const [countries, setCountries] = React.useState([]);
  const [languages, setLanguages] = React.useState([]);
  const [referenceDataLoading, setReferenceDataLoading] = React.useState(true);
  const [showPassword, setShowPassword] = React.useState(false);
  const [consent, setConsent] = React.useState({ consentPortalUpdates: false, consentMarketing: false });
  const [asTherapist, setAsTherapist] = React.useState(false);
  const [therapistFields, setTherapistFields] = React.useState(() => emptyTherapistFields(email));
  const [fieldError, setFieldError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    Promise.all([fetchCountries(), fetchLanguages()])
      .then(([countriesRes, languagesRes]) => {
        if (cancelled) return;
        setCountries(countriesRes?.data || []);
        setLanguages(languagesRes?.data || []);
      })
      .finally(() => {
        if (!cancelled) setReferenceDataLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!countryId) {
      setFieldError(t("auth.registerDetails.countryRequired"));
      return;
    }
    if (!languageId) {
      setFieldError(t("auth.registerDetails.languageRequired"));
      return;
    }
    if (!password) {
      setFieldError(t("auth.registerDetails.passwordRequired"));
      return;
    }
    if (!isPasswordValid(password)) {
      setFieldError(t("auth.registerDetails.passwordRequirements"));
      return;
    }
    if (password !== confirmPassword) {
      setFieldError(t("auth.registerDetails.passwordsMismatch"));
      return;
    }
    if (asTherapist) {
      if (!therapistFields.practiceName.trim() || !therapistFields.practiceCity.trim() || !therapistFields.practiceEmail.trim()) {
        setFieldError(t("auth.registerDetails.therapistFieldsRequired"));
        return;
      }
      if (therapistFields.files.length === 0) {
        setFieldError(t("auth.registerDetails.therapistDocsRequired"));
        return;
      }
    }
    setFieldError("");
    onSubmit({
      password,
      name: name.trim(),
      countryId,
      languageId,
      ...consent,
      asTherapist,
      therapistFields: asTherapist ? therapistFields : null,
    });
  };

  const visibilityToggle = (
    <InputAdornment position="end">
      <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small" sx={{ color: "#6b7280" }}>
        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <form onSubmit={handleSubmit}>
      <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: "1.0625rem", color: AUTH_TEXT_PRIMARY }}>
        {t("auth.registerDetails.title")}
      </Typography>
      <Typography sx={{ mb: 3, fontSize: "0.8125rem", color: AUTH_TEXT_SECONDARY }}>
        {t("auth.registerDetails.subtitle")}
      </Typography>

      <DarkTextField value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auth.registerDetails.namePlaceholder")} disabled={loading} sx={{ mb: 1.5 }} />
      <SearchableSelect
        dark
        value={countryId}
        onChange={setCountryId}
        options={countries}
        loading={referenceDataLoading}
        placeholder={t("auth.registerDetails.countryPlaceholder")}
        disabled={loading}
        sx={{ mb: 1.5 }}
      />
      <SearchableSelect
        dark
        value={languageId}
        onChange={setLanguageId}
        options={languages}
        getOptionLabel={(option) => option?.native_name || option?.name || ""}
        loading={referenceDataLoading}
        placeholder={t("auth.registerDetails.languagePlaceholder")}
        disabled={loading}
        sx={{ mb: 1.5 }}
      />

      <DarkTextField
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        type={showPassword ? "text" : "password"}
        placeholder={t("auth.registerDetails.passwordPlaceholder")}
        disabled={loading}
        InputProps={{ endAdornment: visibilityToggle }}
      />
      <PasswordStrengthChecklist password={password} />
      {Array.isArray(fieldErrors) && fieldErrors.length > 0 && (
        <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.25 }}>
          {fieldErrors.map((msg, i) => (
            <Typography key={i} component="li" variant="caption" sx={{ color: AUTH_ERROR }}>
              {msg}
            </Typography>
          ))}
        </Box>
      )}
      <DarkTextField
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        type={showPassword ? "text" : "password"}
        placeholder={t("auth.registerDetails.confirmPasswordPlaceholder")}
        disabled={loading}
        sx={{ mt: 1.5 }}
      />

      <ConsentCheckboxes
        consentPortalUpdates={consent.consentPortalUpdates}
        consentMarketing={consent.consentMarketing}
        onChange={setConsent}
        sx={{ mt: 1.5 }}
      />

      <Divider sx={{ my: 2.5, borderColor: AUTH_CARD_BORDER }} />

      <FormControlLabel
        sx={{ ml: 0, mr: 0, width: "100%", justifyContent: "space-between" }}
        labelPlacement="start"
        control={
          <Switch
            checked={asTherapist}
            disabled={loading}
            onChange={(e) => setAsTherapist(e.target.checked)}
            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND_BLUE }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: BRAND_BLUE } }}
          />
        }
        label={
          <Box>
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: AUTH_TEXT_PRIMARY }}>
              {t("auth.registerDetails.registerAsTherapist")}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: AUTH_TEXT_SECONDARY }}>
              {t("auth.registerDetails.registerAsTherapistDesc")}
            </Typography>
          </Box>
        }
      />

      {asTherapist && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: `1px solid ${AUTH_CARD_BORDER}`,
            borderRadius: 2,
          }}
        >
          <TherapistApplyFields value={therapistFields} onChange={setTherapistFields} disabled={loading} />
        </Box>
      )}

      {fieldError && (
        <Typography variant="caption" sx={{ color: AUTH_ERROR, mt: 2, display: "block" }}>
          {fieldError}
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <AuthPrimaryButton type="submit" disabled={loading || !isPasswordValid(password)} sx={{ mt: 2.5 }}>
        {loading ? t("auth.registerDetails.submitting") : submitLabel || t("auth.registerDetails.submit")}
      </AuthPrimaryButton>
    </form>
  );
}

export default RegisterDetailsForm;
